import * as THREE from 'three';
import { Logger } from "../core/Logger.js";

/**
 * The MouseButton enum provides constants representing the different mouse buttons.
 */
export const MouseButton = {
  /**
   * Represents the left mouse button.
   */
  Left: 0,
  /**
   * Represents the middle mouse button (often associated with the scroll wheel button).
   */
  Middle: 1,
  /**
   * Represents the right mouse button.
   */
  Right: 2,
  /**
   * The first extended mouse button.
   */
  XButton1: 3,
  /**
   * The second extended mouse button.
   */
  XButton2: 4,
};

/**
 * The MouseInput class is designed to help you handle mouse input in your applications.
 */
export class MouseInput {

  constructor(renderer) {
    this._x = 0;
    this._y = 0;
    this._pressedButtons = new Set();
    this._releasedButtons = new Set();
    this._currentScrollY = 0;
    this._lastScrollY = 0;
    this._frameSnapshot = null;
    this._renderer = renderer;
    this._raycaster = new THREE.Raycaster();
    this._mouse = new THREE.Vector2();
    this._plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), -0.5);
    this._cursorPosition = new THREE.Vector3();
  }

  /**
   * Starts capturing mouse input.
   */
  start() {
    if (typeof window === 'undefined') {
      Logger.warn('Window is not defined. Event listeners cannot be registered.');
      return;
    }

    this.mousemoveHandler = event => {
      this._x = event.clientX;
      this._y = event.clientY;
    };
    window.addEventListener('mousemove', this.mousemoveHandler);

    this.mousedownHandler = event => {
      this._pressedButtons.add(event.button);
      this._releasedButtons.delete(event.button);
    };

    this.mouseupHandler = event => {
      this._pressedButtons.delete(event.button);
      this._releasedButtons.add(event.button);
    };

    window.addEventListener('mousedown', this.mousedownHandler);
    window.addEventListener('mouseup', this.mouseupHandler);

    this.wheelHandler = event => {
      this._currentScrollY += event.deltaY;
    };
    window.addEventListener('wheel', this.wheelHandler);
  }

  /**
   * Stops capturing mouse input by removing all event listeners.
   */
  stop() {
    this._pressedButtons.clear();
    this._releasedButtons.clear();
    window.removeEventListener('mousemove', this.mousemoveHandler);
    window.removeEventListener('mousedown', this.mousedownHandler);
    window.removeEventListener('mouseup', this.mouseupHandler);
    window.removeEventListener('wheel', this.wheelHandler);
  }

  /**
  * Create a frame-consistent snapshot on first query of the frame.
  */
  _ensureSnapshot() {
    if (this._frameSnapshot) return;

    this._frameSnapshot = {
      x: this._x,
      y: this._y,
      pressedButtons: new Set(this._pressedButtons),
      releasedButtons: new Set(this._releasedButtons),
      wheelDelta: this._lastScrollY - this._currentScrollY,
    };

    // Commit scroll value forward
    this._lastScrollY = this._currentScrollY;
  }

  /**
   * Checks if a specific mouse button is currently pressed.
   * @param {*} button The mouse button to check (e.g., MouseButton.Left).
   * @returns true if the button is pressed, false otherwise.
  */
  isButtonPressed(button) {
    this._ensureSnapshot();
    return this._frameSnapshot.pressedButtons.has(button);
  }

  /**
   * Checks if a specific mouse button has been released.
   * @param {*} button The mouse button to check (e.g., MouseButton.Left).
   */
  isButtonReleased(button) {
    this._ensureSnapshot();
    if (this._frameSnapshot.releasedButtons.has(button)) {
      this._releasedButtons.delete(button);
      return true;
    }
    return false;
  }

  /**
   * Computes the mouse position and calculates the raycaster and 
   * cursor position in 3D space.
   */
  raycast(camera) {
    this._ensureSnapshot();
    const rect = this._renderer.domElement.getBoundingClientRect();
    this._mouse.x = ((this._frameSnapshot.x - rect.left) / rect.width) * 2 - 1;
    this._mouse.y = -((this._frameSnapshot.y - rect.top) / rect.height) * 2 + 1;
    this._raycaster.setFromCamera(this._mouse, camera);
    this._raycaster.ray.intersectPlane(this._plane, this._cursorPosition);
    return { position: this._cursorPosition.clone(), raycaster: this._raycaster };
  }

  /**
  * Gets the raw mouse position.
  * @returns An object with x and y properties representing the horizontal and vertical position of the mouse pointer.
  */
  position() {
    this._ensureSnapshot();
    return { x: this._frameSnapshot.x, y: this._frameSnapshot.y };
  }

  /**
  * Gets the change in wheel values (delta) since the last scroll event.
  * Positive values indicate scrolling upward.
  * Negative values indicate scrolling downward.
  */
  wheelDelta() {
    this._ensureSnapshot();
    return this._frameSnapshot.wheelDelta;
  }

  /**
  * Call this at the end of frame
  */
  eventFrameEnd() {
    this._frameSnapshot = null;
  }
}
